// src/components/pages/AccessoriesDetails.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CartProduct, AddIcon, MinusIcon } from "../../assets/icons/icons";
import RelatedAccessories from "../sections/RelatedAccessories";
import { useTranslation } from "react-i18next";

axios.defaults.withCredentials = true;

const PUBLIC_BASE = "https://laravel-api.aj-mart.com";
const STORAGE_BASE = `${PUBLIC_BASE}/storage/app/public/`;

// ===== Utils =====
const PLACEHOLDER =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="24">No Image</text></svg>';

const buildUrl = (path) => {
  if (!path) return PLACEHOLDER;
  const s = String(path).trim();
  if (s.startsWith("http")) return s;
  return STORAGE_BASE + s.replace(/^\/+/, "");
};

const safeParse = (val, fallback) => {
  try {
    if (val == null) return fallback;
    if (Array.isArray(val) || typeof val === "object") return val;
    const parsed = JSON.parse(val);
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
};

const uniq = (arr) => [...new Set(arr.filter(Boolean).map((x) => String(x).trim()))];

const computeDiscountView = (price, discountRaw) => {
  const p = Number(price) || 0;
  const d = Number(discountRaw) || 0;
  if (p <= 0 || d <= 0) return { final: p, percentLabel: 0 };

  let final;
  let percentLabel;
  if (d <= 1) {
    final = +(p * (1 - d)).toFixed(2);
    percentLabel = Math.round(d * 100);
  } else if (d <= 100) {
    final = +(p * (1 - d / 100)).toFixed(2);
    percentLabel = Math.round(d);
  } else {
    final = Math.max(0, +(p - d).toFixed(2));
    percentLabel = Math.round((d / p) * 100);
  }
  return { final, percentLabel };
};

// ===== Color helpers =====
const sanitizeCssColor = (val) => {
  if (!val || typeof val !== "string") return "";
  const s = val.trim();
  if (/^(rgb|hsl)a?\(/i.test(s)) return s;                     // rgb/rgba/hsl/hsla
  if (/^[a-z]+$/i.test(s)) return s;                           // CSS color name
  const hex = s.replace(/^#/, "").trim();
  // 3, 4 (with alpha), 6, 8 (with alpha) hex
  if (/^[0-9a-f]{3}$/i.test(hex) || /^[0-9a-f]{4}$/i.test(hex) ||
      /^[0-9a-f]{6}$/i.test(hex) || /^[0-9a-f]{8}$/i.test(hex)) {
    return `#${hex}`;
  }
  return "";
};

const hexToRgb = (hex) => {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 8) h = h.slice(0, 6);
  if (h.length !== 6) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const isLight = (cssColor) => {
  const rgb = hexToRgb(cssColor || "");
  if (!rgb) return false;
  // relative luminance
  const c = ["r", "g", "b"].map((k) => {
    const v = rgb[k] / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  const L = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return L > 0.8;
};

// Small image component
function Img({ src, alt = "", className = "", eager = false, width, height }) {
  const [err, setErr] = useState(false);
  return (
    <img
      src={err ? PLACEHOLDER : buildUrl(src)}
      alt={alt}
      loading={eager ? "eager" : "lazy"}
      onError={() => setErr(true)}
      width={width}
      height={height}
      className={className}
      draggable={false}
    />
  );
}

// تطبيع ألوان الإكسسوارات
const normalizeAccColors = (acc) => {
  // 1) إذا في accessories_colors_with_hex (Array أو JSON) بنستعمله
  const withHex = safeParse(acc?.accessories_colors_with_hex, null);
  if (Array.isArray(withHex) && withHex.length) {
    return withHex
      .map((c, i) => ({
        id: c?.id ?? c?.color_id ?? i + 1,
        name: String(c?.color_name || c?.name || c?.label || "Color").trim(),
        hex: sanitizeCssColor(c?.color_hex || c?.hex || c?.code || ""),
      }))
      .filter((c) => c.name);
  }

  // 2) fallback: accessories_color ممكن تكون JSON أو CSV بسيتطلع معها [ ] و " "
  let names = safeParse(acc?.accessories_color, null);
  if (!Array.isArray(names)) {
    const raw = String(acc?.accessories_color || "");
    names = raw
      .replace(/^\s*\[|\]\s*$/g, "")                 // شيل الأقواس [] لو موجودة
      .split(",")
      .map((s) => s.replace(/^"+|"+$/g, "").trim())  // شيل الدبل كوتس من كل عنصر
      .filter(Boolean);
  }

  return names.map((n, i) => ({ id: i + 1, name: n, hex: "" }));
};

// Swatch button (كان مستخدم للدوائر — سيبناه كما هو لكنه غير مُستخدم الآن)
function ColorDot({ color, selected, onClick }) {
  const bg = sanitizeCssColor(color?.hex || color?.name || "");
  const showFallback = !bg;
  const light = isLight(bg);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-8 h-8 rounded-full border transition-transform hover:scale-[1.06] ${
        selected ? "ring-2 ring-[#1485a6]" : ""
      }`}
      title={color?.name || "Color"}
      aria-label={color?.name || "Color"}
      style={{
        background: showFallback ? "linear-gradient(45deg,#eee,#ddd)" : bg,
        borderColor: selected ? "#1485a6" : light ? "#cbd5e1" : "#e5e7eb",
      }}
    >
      {selected && (
        <svg
          className="absolute inset-0 m-auto w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke={light ? "#111827" : "#ffffff"}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  );
}

export default function AccessoriesDetails() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();

  const [dataModel, setDataModel] = useState([]);
  const [accessory, setAccessory] = useState(null);
  const [brand, setBrand] = useState(null);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [selectedColorIdx, setSelectedColorIdx] = useState(null);
  const [selectedModels, setSelectedModels] = useState([]);
  const [quantity, setQuantity] = useState(1);

  const [isLoading, setIsLoading] = useState(true);
  const [popup, setPopup] = useState({ show: false, msg: "", type: "success" });

  // CSRF
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${PUBLIC_BASE}/csrf-token`);
        axios.defaults.headers.common["X-CSRF-TOKEN"] = res.data.csrf_token;
      } catch {}
    })();
  }, []);

  // Models (master list)
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${PUBLIC_BASE}/api/accessoriesModel`);
        setDataModel(res?.data?.models || []);
      } catch (e) {
        console.error("Error fetching models", e);
      }
    })();
  }, []);

  // Accessory
  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    let stop = false;
    const fetchAcc = async (retry = 0) => {
      try {
        const res = await axios.get(`${PUBLIC_BASE}/api/accessories/${id}`);
        if (stop) return;
        const data = res?.data?.data;
        setAccessory(data || null);
      } catch (e) {
        if (!stop && retry < 1) setTimeout(() => fetchAcc(retry + 1), 500);
      } finally {
        if (!stop) setIsLoading(false);
      }
    };
    fetchAcc();
    return () => {
      stop = true;
    };
  }, [id]);

  // Brand
  useEffect(() => {
    (async () => {
      if (!accessory?.brand_id) return;
      try {
        const res = await axios.get(`${PUBLIC_BASE}/api/brand/${accessory.brand_id}`);
        setBrand(res?.data?.brand || null);
      } catch {}
    })();
  }, [accessory]);

  // مشتقات
  const accessoriesName = accessory
    ? i18n.language === "ar"
      ? accessory.accessories_name_A
      : accessory.accessories_name_E
    : "";

  const accessoriesDesc = accessory
    ? i18n.language === "ar"
      ? accessory.accessories_desc_A
      : accessory.accessories_desc_E
    : "";

  // صور
  const images = useMemo(() => {
    if (!accessory) return [];
    const subs = safeParse(accessory.accessories_images, []);
    return uniq([accessory.accessories_main_img, ...(Array.isArray(subs) ? subs : [])]);
  }, [accessory]);

  const selectedImage = images[selectedIdx];

  // ألوان
  const colors = useMemo(() => normalizeAccColors(accessory || {}), [accessory]);
  const selectedColor = selectedColorIdx != null ? colors[selectedColorIdx] : null;

  useEffect(() => {
    if (colors.length === 1 && selectedColorIdx == null) setSelectedColorIdx(0);
  }, [colors, selectedColorIdx]);

  // موديلات
  const linkedModelIds = useMemo(() => safeParse(accessory?.accessories_model, []), [accessory]);
  const filteredModels = useMemo(() => {
    const ids = new Set(
      Array.isArray(linkedModelIds) ? linkedModelIds.map((x) => (typeof x === "object" ? x?.model_id : x)) : []
    );
    return dataModel.filter((m) => ids.has(m.model_id));
  }, [dataModel, linkedModelIds]);

  // الأسعار والخصم
  const basePrice = Number(accessory?.accessories_price || 0);
  const discountRaw = Number(accessory?.accessories_price_discount || 0);
  const { final: finalPrice, percentLabel } = computeDiscountView(basePrice, discountRaw);
  const hasDiscount = percentLabel > 0 && finalPrice < basePrice;

  const outOfStock = Number(accessory?.accessories_stock_available) <= 0;
  const requiresColor = colors.length > 0;
  const requiresModel = filteredModels.length > 0;

  const disableAdd =
    outOfStock ||
    (requiresColor && selectedColorIdx == null) ||
    (requiresModel && selectedModels.length === 0);

  const increaseQuantity = () => setQuantity((q) => q + 1);
  const decreaseQuantity = () => setQuantity((q) => (q > 1 ? q - 1 : 1));

  const toggleModelSelection = (model_name) => {
    setSelectedModels((prev) => (prev[0] === model_name ? [] : [model_name]));
  };

  const showPopup = (msg, type = "success", ms = 2200) => {
    setPopup({ show: true, msg, type });
    setTimeout(() => setPopup((p) => ({ ...p, show: false })), ms);
  };

  const handleAddToCart = async () => {
    if (!accessory) return;
    if (requiresColor && selectedColorIdx == null) {
      return showPopup(t("الرجاء اختيار اللون"), "error", 2200);
    }

    const payload = {
      accessories_id: accessory.accessories_id,
      accessories_name_E: accessory.accessories_name_E,
      accessories_name_A: accessory.accessories_name_A,

      // الأسعار
      accessories_price: finalPrice,
      accessories_price_original: basePrice,
      accessories_price_discount: discountRaw,

      accessories_stock_available: parseInt(accessory.accessories_stock_available ?? 0, 10),

      selected_color: selectedColor?.hex || selectedColor?.name || "",
      selected_color_name: selectedColor?.name || "",
      selected_models: selectedModels.length ? selectedModels : null,

      accessories_category: accessory.accessories_category || "",
      accessories_brand_name: accessory.accessories_brand_name || "",
      accessories_tag: accessory.accessories_tag || "",
      accessories_features_E: accessory.accessories_features_E || "",
      accessories_features_A: accessory.accessories_features_A || "",
      accessories_desc_E: accessory.accessories_desc_E || "",
      accessories_desc_A: accessory.accessories_desc_A || "",
      accessories_color: accessory.accessories_color || "",
      accessories_main_img: accessory.accessories_main_img || "",
      accessories_images: accessory.accessories_images || "",
      accessories_username: accessory.accessories_username || "",
      brand_id: accessory.brand_id || "",
      accessories_category_id: accessory.accessories_category_id || "",
      quantity,
    };

    try {
      const res = await axios.post(`${PUBLIC_BASE}/cart/add`, payload, { withCredentials: true });
      showPopup(res?.data?.message || t("تمت الإضافة إلى السلة"), "success", 2200);
    } catch (e) {
      showPopup(e?.response?.data?.message || t("حدث خطأ أثناء إضافة المنتج إلى السلة"), "error", 3000);
    }
  };

  if (isLoading) return <div className="p-6">{t("Loading...")}</div>;
  if (!accessory) return <div className="p-6">{t("Product not found")}</div>;

  return (
    <>
      {/* Popup */}
      {popup.show && (
        <div className="fixed inset-0 flex items-center justify-center z-[60] bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-[350px] flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center text-white ${
                popup.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              {popup.type === "success" ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            <div className={`text-sm font-medium ${popup.type === "success" ? "text-green-700" : "text-red-700"}`}>
              {popup.msg}
            </div>
            <button onClick={() => setPopup((p) => ({ ...p, show: false }))} className="text-gray-500 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <section className="product-details py-10">
        <div className="container">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Gallery */}
            <div className="lg:w-[40%]">
              <div className="sticky top-24">
                <div className="w-full h-[300px] md:h-[380px] border border-[#6663] rounded-lg overflow-hidden bg-white">
                  <Img
                    src={selectedImage}
                    alt={accessoriesName}
                    className="w-full h-full object-contain hover:scale-[1.015] transition-transform"
                    eager
                    width="800"
                    height="600"
                  />
                </div>

                {images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {images.map((img, i) => (
                      <button
                        key={`${img}-${i}`}
                        onClick={() => setSelectedIdx(i)}
                        className={`border rounded-md p-1 w-[64px] h-[64px] bg-white ${
                          selectedIdx === i ? "border-[#1485a6]" : "border-[#6663]"
                        }`}
                        title={t("Change image")}
                        type="button"
                      >
                        <Img src={img} alt="" className="w-full h-full object-contain" width="160" height="160" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="lg:w-[60%] flex flex-col gap-6">
              <p className="text-sm text-[#6668]">
                {t("Shop from a wide range of electronics, mobile devices and accessories from aj mart")}
              </p>

              <div className="flex items-center gap-3">
                {brand?.brand_img ? (
                  <Img className="w-[38px] h-[38px] object-contain bg-white border border-[#0001] rounded" src={brand.brand_img} alt={brand.brand_name} width="38" height="38" />
                ) : (
                  <div className="w-[38px] h-[38px] bg-gray-200 rounded" />
                )}
                {brand?.brand_name && <span className="text-sm text-gray-600">{brand.brand_name}</span>}
              </div>

              <div className="flex flex-col gap-2">
                <h1 className="text-2xl font-bold leading-snug">{accessoriesName}</h1>
                <div className={`font-bold ${outOfStock ? "text-red-500" : "text-[#18AB00]"}`}>
                  {t(outOfStock ? "Out of Stock" : "In Stock")}
                </div>
              </div>

              {/* Colors (نفس ستايل صفحة المنتج: أزرار بأسماء فقط) */}
              {colors.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-semibold">
                    {t("Colors")}
                    {selectedColor ? `: ${selectedColor.name}` : ""}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    {colors.map((c, idx) => (
                      <button
                        key={`${c.name}-${idx}`}
                        onClick={() => setSelectedColorIdx(idx)}
                        className={`flex items-center gap-2 border rounded-full px-3 py-1 transition 
                          ${selectedColorIdx === idx ? "border-[#1485a6] ring-2 ring-[#1485a6]/20" : "border-transparent bg-black/5"}
                        `}
                        title={c.name}
                        type="button"
                      >
                        <span className="text-sm">{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Models multi-select (chips) */}
              {filteredModels.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm font-semibold">{t("Select Models")}</div>
                  <div className="flex flex-wrap gap-2">
                    {filteredModels.map((m) => {
                      const active = selectedModels.includes(m.model_name);
                      return (
                        <button
                          key={m.model_id}
                          type="button"
                          onClick={() => toggleModelSelection(m.model_name)}
                          className={`px-3 py-1.5 rounded-full text-sm border transition ${
                            active ? "bg-[#1485a6] text-white border-[#1485a6]" : "bg-[#6662] text-black border-transparent hover:bg-[#6663]"
                          }`}
                          title={m.model_name}
                        >
                          {t(m.model_name)}
                        </button>
                      );
                    })}
                  </div>

                  {selectedModels.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedModels.map((name) => (
                        <span key={name} className="px-2 py-0.5 rounded-full bg-[#1485a6]/10 text-[#1485a6] text-xs border border-[#1485a6]/30">
                          {t(name)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Discount badge */}
              {hasDiscount && (
                <div className="bg-[#1485a6]/90 w-fit text-white font-bold px-4 py-1.5 rounded-full text-sm">
                  {percentLabel}% {t("Discount")}
                </div>
              )}

              {/* Price + CTA */}
              <div className="w-full sm:w-[380px] border border-[#0001] p-4 rounded-lg bg-white shadow-sm">
                <div className="flex justify-between items-center">
                  <div className="text-sm">{t("Delivery")}:</div>
                  <div className="bg-[rgb(13,166,120,0.12)] text-[#0DA678] font-bold px-3 py-0.5 rounded-full text-xs">
                    1 {t("JOD")}
                  </div>
                </div>

                <div className="flex flex-col items-center gap-1 my-5">
                  {!hasDiscount ? (
                    <span className="font-bold text-2xl">
                      {basePrice} {t("JOD")}
                    </span>
                  ) : (
                    <>
                      <span className="line-through text-gray-400">{basePrice} {t("JOD")}</span>
                      <span className="font-bold text-2xl text-red-600">
                        {finalPrice} {t("JOD")}
                      </span>
                      <span className="text-xs opacity-80">({t("After Discount")})</span>
                    </>
                  )}
                </div>

                <div className="border border-[#0001] rounded-md flex items-center justify-between px-5 py-2">
                  <button onClick={increaseQuantity} className="p-1" aria-label={t("Increase")} type="button">
                    <AddIcon />
                  </button>
                  <span className="font-semibold">{quantity}</span>
                  <button onClick={decreaseQuantity} className="p-1" aria-label={t("Decrease")} type="button">
                    <MinusIcon />
                  </button>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={disableAdd}
                  className={`mt-3 w-full bg-[#1485a6] text-white font-bold rounded-md py-2.5 flex items-center justify-center gap-2 transition ${
                    disableAdd ? "opacity-50 cursor-not-allowed" : "hover:opacity-90"
                  }`}
                  type="button"
                >
                  <CartProduct />
                  {t("Add To Cart")}
                </button>

                {outOfStock && <div className="text-xs text-amber-600 mt-2">{t("المنتج غير متوفر حالياً")}</div>}
                {!outOfStock && requiresColor && selectedColorIdx == null && (
                  <div className="text-xs text-amber-600 mt-2">{t("الرجاء اختيار اللون")}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      {accessoriesDesc && (
        <section className="relative pb-10">
          <div className="container">
            <div className="border border-[#0001] rounded-md overflow-hidden">
              <div className="text-[20px] font-bold capitalize px-5 py-3 bg-white text-[#1485a6] border-b border-[#0001]">
                {t("Description")}
              </div>
              <div className="p-5 bg-white">
                <p className="text-[14px] text-gray-600 leading-7">{accessoriesDesc}</p>
              </div>
            </div>
          </div>
        </section>
      )}

      <RelatedAccessories />
    </>
  );
}
